import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormProperty } from '@common/components/app-control/app-control.component';
import { InputType } from '@common/components/input/input.type';
import { environment } from '@environments/environment';
import { NumberFormatOptions } from '@progress/kendo-angular-intl';
import { CommonModule as AppCommonModule } from 'app/common/common.module';

interface TestCase {
    name: string;
    description: string;
    value: number | null;
    componentType: 'app-control' | 'number-input';
    format?: string | NumberFormatOptions;
    decimals?: number;
    min?: number;
    max?: number;
}

@Component({
    selector: 'app-test-number-inputs',
    templateUrl: './test-number-inputs.component.html',
    styleUrls: ['./test-number-inputs.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, AppCommonModule]
})
export class TestNumberInputsComponent {
    testCases = signal<TestCase[]>([
        // App Control Component Tests
        {
            name: 'App Control - String Format (#)',
            description: 'Integer format with no thousands separator',
            value: 1234,
            componentType: 'app-control',
            format: '#',
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - String Format (n2)',
            description: '2 decimals with thousands separator',
            value: 1234.56,
            componentType: 'app-control',
            format: 'n2',
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - String Format (#,##0.## kW)',
            description: '2 decimals with thousands separator',
            value: 1234.56,
            componentType: 'app-control',
            format: '#,##0.## kW',
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - Limit Decimals',
            description: 'Decimals property limited to 3',
            value: 123.456,
            componentType: 'app-control',
            format: 'n2',
            decimals: 3,
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - Currency Default (EUR, 2 decimals)',
            description: 'Currency format using default options',
            value: 1234.5678,
            decimals: 4,
            componentType: 'app-control',
            format: environment.settings.appControl.currencyFormat(),
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - Currency USD',
            description: 'Currency format with USD',
            value: 1234.5678,
            componentType: 'app-control',
            format: environment.settings.appControl.currencyFormat({ currency: 'USD' }),
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - Currency EUR (4 decimals)',
            description: 'Currency format with 4 decimals',
            value: 1234.5678,
            componentType: 'app-control',
            format: environment.settings.appControl.currencyFormat({ maximumFractionDigits: 4 }),
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - Currency USD (3 decimals)',
            description: 'Currency format with USD and 3 decimals',
            value: 1234.5678,
            componentType: 'app-control',
            format: environment.settings.appControl.currencyFormat({ currency: 'USD', maximumFractionDigits: 3 }),
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - Currency Code Display',
            description: 'Currency format with code display instead of symbol',
            value: 1234.5678,
            componentType: 'app-control',
            format: environment.settings.appControl.currencyFormat({ currency: 'USD', currencyDisplay: 'code' }),
            min: 0,
            max: 10000
        },
        {
            name: 'App Control - Custom NumberFormatOptions',
            description: 'Custom NumberFormatOptions with 3 decimals',
            value: 1234.567,
            componentType: 'app-control',
            format: {
                style: 'decimal',
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
                useGrouping: true
            } as NumberFormatOptions,
            min: 0,
            max: 10000
        },

        // Number Input Component Tests
        {
            name: 'Number Input - String Format (#)',
            description: 'Integer format with no thousands separator',
            value: 1234,
            componentType: 'number-input',
            format: '#',
            min: 0,
            max: 10000
        },
        {
            name: 'Number Input - String Format (n2)',
            description: '2 decimals with thousands separator',
            value: 1234.56,
            componentType: 'number-input',
            format: 'n2',
            min: 0,
            max: 10000
        },
        {
            name: 'Number Input - String Format (#,##0.## kW)',
            description: '2 decimals with thousands separator',
            value: 1234.56,
            componentType: 'number-input',
            format: '#,##0.## kWh',
            min: 0,
            max: 10000
        },
        {
            name: 'Number Input - Decimals',
            description: 'Decimals property limits editable decimals',
            value: 123.456,
            componentType: 'number-input',
            format: 'n2',
            decimals: 3,
            min: 0,
            max: 10000
        },
        {
            name: 'Number Input - Currency Default (EUR, 2 decimals)',
            description: 'Currency format using default options',
            value: 1234.5678,
            decimals: 4,
            componentType: 'number-input',
            format: environment.settings.appControl.currencyFormat(),
            min: 0,
            max: 10000
        },
        {
            name: 'Number Input - Currency USD',
            description: 'Currency format with USD',
            value: 1234.5678,
            componentType: 'number-input',
            format: environment.settings.appControl.currencyFormat({ currency: 'USD' }),
            min: 0,
            max: 10000
        },
        {
            name: 'Number Input - Currency EUR (4 decimals)',
            description: 'Currency format with 4 decimals',
            value: 1234.5678,
            componentType: 'number-input',
            format: environment.settings.appControl.currencyFormat({ maximumFractionDigits: 4 }),
            min: 0,
            max: 10000
        },
        {
            name: 'Number Input - Currency Code Display',
            description: 'Currency format with code display instead of symbol',
            value: 1234.5678,
            componentType: 'number-input',
            format: environment.settings.appControl.currencyFormat({ currency: 'USD', currencyDisplay: 'code' }),
            min: 0,
            max: 10000
        },
        {
            name: 'Number Input - Custom NumberFormatOptions',
            description: 'Custom NumberFormatOptions with 3 decimals',
            value: 1234.567,
            componentType: 'number-input',
            format: {
                style: 'decimal',
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
                useGrouping: true
            } as NumberFormatOptions,
            min: 0,
            max: 10000
        },

        // Percentage Format Tests
        {
            name: 'App Control - Percentage String Format (#.## \\\\%)',
            description: '',
            value: 12.34,
            componentType: 'app-control',
            format: '#.## \\%',
            min: 0,
            max: 100
        },
        {
            name: 'App Control - Percentage NumberFormatOptions (0-1)',
            description: 'Percentage using NumberFormatOptions with percent style (0-1 range)',
            value: 0.1234,
            componentType: 'app-control',
            format: {
                style: 'percent',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            } as NumberFormatOptions,
            min: 0,
            max: 1
        },
        {
            name: 'Number Input - Percentage String Format (#.## \\\\%)',
            description: 'Percentage with string format',
            value: 12.34,
            componentType: 'number-input',
            format: '#.## \\%',
            decimals: 2,
            min: 0,
            max: 100
        },
        {
            name: 'Number Input - Percentage NumberFormatOptions (0-1)',
            description: 'Percentage using NumberFormatOptions with percent style',
            value: 0.1234,
            componentType: 'number-input',
            format: {
                style: 'percent',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            } as NumberFormatOptions,
            min: 0,
            max: 1
        }
    ]);

    // For app-control component
    getDialogProperty(testCase: TestCase): FormProperty {
        return {
            name: 'value',
            label: testCase.name,
            type: 'number',
            format: testCase.format,
            decimals: testCase.decimals,
            min: testCase.min,
            max: testCase.max,
            colSpan: 12
        };
    }

    // For number-input component
    getInputConfig(testCase: TestCase) {
        return {
            type: InputType.NUMBER,
            id: `test-${testCase.name.replace(/\s/g, '-').toLowerCase()}`,
            label: testCase.name,
            format: testCase.format,
            decimals: testCase.decimals,
            min: testCase.min,
            max: testCase.max
        };
    }

    onValueChange(testCase: TestCase, newValue: number) {
        testCase.value = newValue;
        console.log(`${testCase.name}: ${newValue}`);
    }

    resetValues() {
        this.testCases.update((cases) =>
            cases.map((testCase) => ({
                ...testCase,
                value: Math.round(Math.random() * 9999 + 1) / 100
            }))
        );
    }

    clearValues() {
        this.testCases.update((cases) =>
            cases.map((testCase) => ({
                ...testCase,
                value: null
            }))
        );
    }

    getFormatType(format: string | NumberFormatOptions | undefined): string {
        if (typeof format === 'object' && format !== null) {
            return 'NumberFormatOptions';
        }
        return format?.toString() || 'undefined';
    }

    getDecimalsDisplay(decimals: number | undefined): string {
        return decimals?.toString() || 'undefined';
    }

    isNumberFormatOptions(format: string | NumberFormatOptions | undefined): boolean {
        return typeof format === 'object' && format !== null;
    }

    getInputId(name: string): string {
        const id = 'test-' + name.replace(/\s/g, '-').toLowerCase();
        console.log(`Generated ID for "${name}": "${id}"`);
        return id;
    }
}
